import { useState, type KeyboardEvent, type ReactNode } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { VideoMetadataThumbnail } from "../../../document-management/components/shared/VideoMetadataThumbnail";
import type { StepVideoBinding } from "../../utils/ragChatUtils";
import { videoDisplayName } from "../../utils/ragChatUtils";
import { COLLAGE_H } from "./ImageCollage";

const THUMB_COL_W = 72;
const COLLAGE_GAP = 6;

interface StepInlineVideosProps {
  videos: StepVideoBinding[];
  /** Compact layout for step cards; full width in dialogs. */
  compact?: boolean;
}

function clipLabel(
  binding: StepVideoBinding,
  index: number,
  fallback: (num: number) => string
): string {
  return (
    binding.item.name?.trim() ||
    videoDisplayName(binding.item.path, fallback(binding.videoNum || index + 1))
  );
}

function PlusBadge({
  label,
  onClick,
}: {
  label: string;
  onClick: (e: { stopPropagation: () => void }) => void;
}) {
  return (
    <Box
      component="span"
      role="button"
      tabIndex={0}
      aria-label={label}
      data-testid="step-video-plus-badge"
      onClick={onClick}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          onClick(e);
        }
      }}
      sx={{
        position: "absolute",
        right: 8,
        bottom: 8,
        minWidth: 32,
        height: 28,
        px: 1,
        border: "none",
        borderRadius: 999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0,0,0,0.62)",
        color: "common.white",
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
        zIndex: 3,
        cursor: "pointer",
        "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      {label}
    </Box>
  );
}

function PlayMark({ compact }: { compact: boolean }) {
  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          width: compact ? 36 : 40,
          height: compact ? 36 : 40,
          borderRadius: "50%",
          bgcolor: "secondary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
        }}
      >
        <PlayArrowIcon
          sx={{ color: "common.white", fontSize: compact ? 20 : 22, ml: "2px" }}
        />
      </Box>
    </Box>
  );
}

function VideoTile({
  binding,
  compact,
  ariaLabel,
  onOpen,
  sx,
  badge,
}: {
  binding: StepVideoBinding;
  compact: boolean;
  ariaLabel: string;
  onOpen: () => void;
  sx?: object;
  badge?: ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      aria-label={ariaLabel}
      onClick={onOpen}
      sx={{
        position: "relative",
        p: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.25,
        overflow: "hidden",
        bgcolor: "#eef3f8",
        cursor: "pointer",
        display: "block",
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        textAlign: "left",
        "& video": {
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          pointerEvents: "none",
        },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
        ...sx,
      }}
    >
      <VideoMetadataThumbnail src={binding.item.path} fallback={null} />
      <PlayMark compact={compact} />
      {badge}
    </Box>
  );
}

export function StepInlineVideos({ videos, compact = false }: StepInlineVideosProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState<{ title: string; src: string } | null>(null);

  if (!videos.length) return null;

  const moreLabel = (count: number) =>
    t("opva.chatAssistanceMorePhotos", { count });
  const fallback = (num: number) => t("opva.chatAssistanceVideoLabel", { num });

  const openBinding = (binding: StepVideoBinding, index: number) => {
    setOpen({ title: clipLabel(binding, index, fallback), src: binding.item.path });
  };

  const main = videos[0]!;
  let collage: ReactNode;

  if (videos.length === 1) {
    collage = (
      <Box
        data-testid="step-video-collage"
        sx={{ width: "100%", height: COLLAGE_H }}
      >
        <VideoTile
          binding={main}
          compact={compact}
          ariaLabel={clipLabel(main, 0, fallback)}
          onOpen={() => openBinding(main, 0)}
        />
      </Box>
    );
  } else if (videos.length === 2) {
    collage = (
      <Box
        data-testid="step-video-collage"
        sx={{ width: "100%", height: COLLAGE_H }}
      >
        <VideoTile
          binding={main}
          compact={compact}
          ariaLabel={clipLabel(main, 0, fallback)}
          onOpen={() => openBinding(main, 0)}
          badge={
            <PlusBadge
              label={moreLabel(1)}
              onClick={(e) => {
                e.stopPropagation();
                openBinding(videos[1]!, 1);
              }}
            />
          }
        />
      </Box>
    );
  } else {
    const showOverflow = videos.length > 3;
    const overflowCount = videos.length - 2;
    collage = (
      <Box
        data-testid="step-video-collage"
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: `minmax(0, 1fr) min(${THUMB_COL_W}px, 28%)`,
            sm: `1fr ${THUMB_COL_W}px`,
          },
          gridTemplateRows: `${(COLLAGE_H - COLLAGE_GAP) / 2}px ${(COLLAGE_H - COLLAGE_GAP) / 2}px`,
          gap: `${COLLAGE_GAP}px`,
          width: "100%",
          height: COLLAGE_H,
          minWidth: 0,
        }}
      >
        <VideoTile
          binding={main}
          compact={compact}
          ariaLabel={clipLabel(main, 0, fallback)}
          onOpen={() => openBinding(main, 0)}
          sx={{ gridRow: "1 / 3" }}
        />
        <VideoTile
          binding={videos[1]!}
          compact
          ariaLabel={clipLabel(videos[1]!, 1, fallback)}
          onOpen={() => openBinding(videos[1]!, 1)}
        />
        <VideoTile
          binding={videos[2]!}
          compact
          ariaLabel={clipLabel(videos[2]!, 2, fallback)}
          onOpen={() => openBinding(videos[2]!, 2)}
          badge={
            showOverflow ? (
              <PlusBadge
                label={moreLabel(overflowCount)}
                onClick={(e) => {
                  e.stopPropagation();
                  openBinding(videos[3] ?? videos[2]!, videos[3] ? 3 : 2);
                }}
              />
            ) : undefined
          }
        />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mt: compact ? 1 : 1.5, width: "100%" }}>{collage}</Box>

      <Dialog open={!!open} onClose={() => setOpen(null)} maxWidth="md" fullWidth>
        <DialogTitle>{open?.title}</DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {open?.src ? (
            <Box
              component="video"
              controls
              src={open.src}
              sx={{ width: "100%", maxHeight: 420, display: "block", bgcolor: "common.black" }}
            />
          ) : (
            <Typography variant="bodySmall" color="text.secondary" sx={{ p: 2 }}>
              {t("opva.chatAssistanceVideoUnavailable")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(null)}>{t("opva.chatAssistanceClose")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
